import { defineStore } from 'pinia';
import CaptainPreferencesAPI from 'dashboard/api/captain/preferences';

export const useCaptainConfigStore = defineStore('captainConfig', {
  state: () => ({
    providers: {},
    models: {},
    features: {},
    lastFetchedAccountId: null,
    llmConnection: {
      provider: 'openai',
      configured: false,
      maskedApiKey: null,
      updatedAt: null,
    },
    anthropicConnection: {
      provider: 'anthropic',
      configured: false,
      maskedApiKey: null,
      updatedAt: null,
    },
    uiFlags: {
      isFetching: false,
    },
  }),

  getters: {
    getProviders: state => state.providers,
    getModels: state => state.models,
    getFeatures: state => state.features,
    getLlmConnection: state => state.llmConnection,
    getAnthropicConnection: state => state.anthropicConnection,
    getUIFlags: state => state.uiFlags,
    getModelsForFeature: state => featureKey => {
      const feature = state.features[featureKey];
      const models = feature?.models || [];

      const providerOrder = { openai: 0, anthropic: 1, gemini: 2 };

      return [...models].sort((a, b) => {
        // Move coming_soon items to the end
        if (a.coming_soon && !b.coming_soon) return 1;
        if (!a.coming_soon && b.coming_soon) return -1;

        // Sort by provider
        const providerA = providerOrder[a.provider] ?? 999;
        const providerB = providerOrder[b.provider] ?? 999;
        if (providerA !== providerB) return providerA - providerB;

        // Sort by credit_multiplier (highest first)
        return (b.credit_multiplier || 0) - (a.credit_multiplier || 0);
      });
    },
    getDefaultModelForFeature: state => featureKey => {
      const feature = state.features[featureKey];
      return feature?.default || null;
    },
    getSelectedModelForFeature: state => featureKey => {
      const feature = state.features[featureKey];
      return feature?.selected || feature?.default || null;
    },
  },

  actions: {
    async fetch(options = {}) {
      const accountId = Number(options?.accountId) || null;
      const force = Boolean(options?.force);

      if (
        accountId &&
        !force &&
        this.lastFetchedAccountId === accountId &&
        Object.keys(this.features || {}).length > 0
      ) {
        return;
      }

      this.uiFlags.isFetching = true;
      try {
        const response = await CaptainPreferencesAPI.get();
        this.providers = response.data.providers || {};
        this.models = response.data.models || {};
        this.features = response.data.features || {};
        this.lastFetchedAccountId = accountId;
        const llmConnection = response.data.llm_connection || {};
        this.llmConnection = {
          provider: llmConnection.provider || 'openai',
          configured: Boolean(llmConnection.configured),
          maskedApiKey: llmConnection.masked_api_key || null,
          updatedAt: llmConnection.updated_at || null,
        };
        const anthropicConn = response.data.anthropic_connection || {};
        this.anthropicConnection = {
          provider: anthropicConn.provider || 'anthropic',
          configured: Boolean(anthropicConn.configured),
          maskedApiKey: anthropicConn.masked_api_key || null,
          updatedAt: anthropicConn.updated_at || null,
        };
      } catch (error) {
        // Ignore error
      } finally {
        this.uiFlags.isFetching = false;
      }
    },

    async updatePreferences(data) {
      const response = await CaptainPreferencesAPI.updatePreferences(data);
      this.providers = response.data.providers || {};
      this.models = response.data.models || {};
      this.features = response.data.features || {};
      const llmConnection = response.data.llm_connection || {};
      this.llmConnection = {
        provider: llmConnection.provider || 'openai',
        configured: Boolean(llmConnection.configured),
        maskedApiKey: llmConnection.masked_api_key || null,
        updatedAt: llmConnection.updated_at || null,
      };
      const anthropicConn = response.data.anthropic_connection || {};
      this.anthropicConnection = {
        provider: anthropicConn.provider || 'anthropic',
        configured: Boolean(anthropicConn.configured),
        maskedApiKey: anthropicConn.masked_api_key || null,
        updatedAt: anthropicConn.updated_at || null,
      };
    },
  },
});
